/**
 * DocumentList 组件
 * 从 AZ_AI_MVR_H5/src/pages/OpsMate/components/DocumentList/index.tsx 迁移
 */
import { DownloadOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import {
  App,
  Button,
  Checkbox,
  ConfigProvider,
  Divider,
  Flex,
  Input,
  List,
  Modal,
  Space,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import { createStyles } from "antd-style";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DocumentXlsIcon from "@/assets/opsmate/document_xls.svg";
import DocumentDocIcon from "@/assets/opsmate/document_doc.svg";
import DocumentPdfIcon from "@/assets/opsmate/document_pdf.svg";
import DocumentJpgIcon from "@/assets/opsmate/document_jpg.svg";
import { searchDocumentList } from "@/services/opsmate/documents";
import {
  getAskScope,
  resolveEffectiveCategoryIds,
  resolveEffectiveStudySourceIds,
  type AskScope,
} from "@/services/opsmate/askScope";
import { getLatestDocumentSyncLog } from "@/services/documentSyncLog";
import { useOpsMateContext } from "@/contexts/useOpsMateContext";
import { formatFileSize } from "@/utils/formatters";
import { resolveDownloadUrl } from "@/utils/apiUrl";
import {
  buildAddSourcesSearchParams,
  buildDisplayDocuments,
  collectSessionDocuments,
  type DocumentSourceLike,
  getDocumentDataSource,
  getDocumentDataSourceLabel,
  getDocumentDataSourceLink,
  getDocumentCategoryIds,
  getDocumentGuid,
  getDocumentName,
  isInDocumentList,
  isValidDocumentSource,
} from "./DocumentList.helpers";
import { syncUserSelectedSources } from "./DocumentList.selection";
import { isPendingConversationMigration } from "@/contexts/OpsMateContext.sourceSelection";
import AddSourcesScopeFilters from "./AddSourcesScopeFilters";

const useStyles = createStyles(({ css }) => ({
  title: { color: "#111", fontWeight: 600 },
  sourceItem: css`
    border: none !important;
    &:hover {
      background: #f8f8f8 !important;
    }
  `,
  sourceItemChecked: css`
    background: transparent !important;
    &:hover {
      background: #f8f8f8 !important;
    }
  `,
  hideScrollbar: css`
    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
    -ms-overflow-style: none;
    overflow: -moz-scrollbars-none;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  `,
}));

interface SearchDocItem {
  id: string;
  name: string;
  size: string;
  extension: string;
  updatedAt: string;
  fileUrl?: string;
  dataSource?: string;
  dataSourceLabel?: string;
  dataSourceLink?: ReturnType<typeof getDocumentDataSourceLink>;
  fileSrcUrl?: string;
  sourcePathAskId?: string;
  docCategoryIds: string[];
}

type ManualSource = {
  name: string;
  size?: string;
  uploadedAt?: string;
  fileUrl?: string;
  guidName?: string;
  sourcePathAskId?: string;
  docCategoryIds?: string[];
  dataSource?: string;
};

const getDocName = getDocumentName;

export default function DocummentList() {
  const { styles } = useStyles();
  const { message } = App.useApp();
  const { t } = useTranslation();
  const [lastSyncUpdateTime, setLastSyncUpdateTime] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getLatestDocumentSyncLog()
      .then((res) => {
        if (!cancelled) setLastSyncUpdateTime(res.data?.UpdateTime ?? null);
      })
      .catch(() => {
        if (!cancelled) setLastSyncUpdateTime(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const lastSyncTimeText = useMemo(() => {
    if (!lastSyncUpdateTime) return "";
    const d = new Date(lastSyncUpdateTime);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }, [lastSyncUpdateTime]);
  const messageRef = useRef(message);
  messageRef.current = message;

  const {
    selectedKeys,
    pageHeight,
    items,
    addSourcesVisible,
    setAddSourcesVisible,
    pendingConversationId,
    setHasSelectedSources,
    selectedSourceIndexes: selectedSources,
    setSelectedSourceIndexes: setSelectedSources,
    manualSources,
    setManualSources,
    setSelectedSourceGuidNames,
    setSelectedSourcesCount,
    setAvailableSourcesCount,
    highlightedDocGuid,
    setHighlightedDocGuid,
    docsCollapsed,
    setDocsCollapsed,
  } = useOpsMateContext();

  const latestDocs = useMemo<DocumentSourceLike[]>(() => {
    const list = Array.isArray(items) ? items : [];
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const maybeDocs = (list[i] as any)?.documents;
      if (Array.isArray(maybeDocs)) {
        // 右侧边栏仅展示在 DB DocumentList 中存在的文档（正文 References 区仍展示全部）
        return maybeDocs.filter((x) => {
          return isValidDocumentSource(x) && isInDocumentList(x);
        });
      }
    }
    return [];
  }, [items]);

  // 收集整个 session 中所有引用过的文档（历史文档）；同样仅保留 DB DocumentList 中存在的
  const sessionDocs = useMemo(() => {
    return collectSessionDocuments(Array.isArray(items) ? items : []).filter(isInDocumentList);
  }, [items]);

  const displayDocs = useMemo(() => {
    return buildDisplayDocuments({
      latestDocs,
      sessionDocs,
      manualSources: manualSources || [],
    });
  }, [latestDocs, sessionDocs, manualSources]);

  // 把高亮的文档标识（guid 优先、回退到文档名）解析为当前清单中的下标，避免正文角标与
  // 过滤后的边栏清单下标错位
  const highlightedDocIndex = useMemo(() => {
    if (!highlightedDocGuid) return -1;
    return displayDocs.findIndex(
      (d) => (getDocumentGuid(d) || getDocName(d)) === highlightedDocGuid,
    );
  }, [highlightedDocGuid, displayDocs]);

  const formatDateYMD = (raw?: string): string => {
    if (!raw) return "";
    const datePart = raw.slice(0, 10).replace(/\./g, "-");
    const match = datePart.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
    if (!match) return raw;
    const [, y, m, d] = match;
    return `${y}/${m}/${d}`;
  };

  const getDocTooltipTitle = (doc: any): React.ReactNode => {
    const name = getDocName(doc);
    if (typeof doc === "string") return name || "—";
    const parts: string[] = [];
    if (name) parts.push(name);
    if (doc.size) parts.push(doc.size);
    if (doc.uploadedAt) parts.push(formatDateYMD(doc.uploadedAt));
    const dataSourceLabel = getDocumentDataSourceLabel(getDocumentDataSource(doc));
    if (dataSourceLabel) parts.push(dataSourceLabel);
    return parts.join(" | ") || "—";
  };

  const getDocMetadataText = (doc: any): string => {
    if (typeof doc === "string") return "";
    const parts: string[] = [];
    if (doc.size) parts.push(doc.size);
    if (doc.uploadedAt) parts.push(formatDateYMD(doc.uploadedAt));
    const dataSourceLabel = getDocumentDataSourceLabel(getDocumentDataSource(doc));
    if (dataSourceLabel) parts.push(dataSourceLabel);
    return parts.join(" | ");
  };

  const getDocIcon = (name: string) => {
    const lower = name.toLowerCase();
    if (lower.endsWith(".xlsx") || lower.endsWith(".xls"))
      return (
        <img
          src={DocumentXlsIcon}
          alt=""
          style={{ width: 24, height: 24, flexShrink: 0 }}
          aria-hidden
        />
      );
    if (lower.endsWith(".docx") || lower.endsWith(".doc"))
      return (
        <img
          src={DocumentDocIcon}
          alt=""
          style={{ width: 24, height: 24, flexShrink: 0 }}
          aria-hidden
        />
      );
    if (lower.endsWith(".pdf"))
      return (
        <img
          src={DocumentPdfIcon}
          alt=""
          style={{ width: 24, height: 24, flexShrink: 0 }}
          aria-hidden
        />
      );
    if (lower.endsWith(".jpg") || lower.endsWith(".jpeg"))
      return (
        <img
          src={DocumentJpgIcon}
          alt=""
          style={{ width: 24, height: 24, flexShrink: 0 }}
          aria-hidden
        />
      );
    return (
      <img
        src={DocumentDocIcon}
        alt=""
        style={{ width: 24, height: 24, flexShrink: 0 }}
        aria-hidden
      />
    );
  };

  const canShowDownload = (doc: any): boolean => {
    if (typeof doc === "object" && doc.canDownload === false) return false;
    return true;
  };

  const getDocUrl = (doc: any): string | undefined => {
    if (typeof doc === "string") return undefined;
    return doc.fileUrl;
  };

  const docItemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // 右侧文档勾选独立于“仅使用已选文档”和底部三个库胶囊。
  const setUserSelectedSources = useCallback(
    (next: Set<number>) => {
      syncUserSelectedSources(next, setSelectedSources);
    },
    [setSelectedSources],
  );

  // latest / manual 文档的派生选中态用这两个 ref 记忆“上一次的标识”，避免重复重算。
  const prevLatestDocsKeysRef = useRef<Map<string, string>>(new Map());
  const prevManualSourcesKeysRef = useRef<Map<string, string>>(new Map());

  // 各会话分别记忆引用/手动来源的派生标识；切回旧会话时不重新默认勾选，从而保留用户
  // 上次手动调整的 checkbox。首问 add -> 真实 ID 时同步迁移标识，避免把未选文档重新全选。
  const prevSelectedKeysRef = useRef(selectedKeys);
  useEffect(() => {
    const previousSelectedKey = prevSelectedKeysRef.current;
    prevSelectedKeysRef.current = selectedKeys;
    if (isPendingConversationMigration(previousSelectedKey, selectedKeys, pendingConversationId)) {
      const migrateDerivedKey = (keys: Map<string, string>) => {
        if (!keys.has(previousSelectedKey)) return;
        keys.set(selectedKeys, keys.get(previousSelectedKey)!);
        keys.delete(previousSelectedKey);
      };
      migrateDerivedKey(prevLatestDocsKeysRef.current);
      migrateDerivedKey(prevManualSourcesKeysRef.current);
    }
  }, [pendingConversationId, selectedKeys]);

  // 引用标记点击后滚动到对应文档并高亮 (T8)
  useEffect(() => {
    if (highlightedDocIndex >= 0 && highlightedDocIndex < displayDocs.length) {
      const el = docItemRefs.current.get(highlightedDocIndex);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightedDocIndex, displayDocs.length]);

  // 当 latestDocs（当前消息引用的文档）变化时，选中这些文档；历史文档不选中
  useEffect(() => {
    const latestKey = latestDocs
      .map((d) => getDocumentGuid(d) || getDocName(d))
      .sort()
      .join("||");
    const previousLatestKey = prevLatestDocsKeysRef.current.get(selectedKeys) ?? "";
    if (latestKey !== previousLatestKey) {
      prevLatestDocsKeysRef.current.set(selectedKeys, latestKey);
      setSelectedSources((prev) => {
        const next = new Set<number>();
        displayDocs.forEach((doc, idx) => {
          const docGuid = getDocumentGuid(doc);
          const name = getDocName(doc);
          const isInLatest = latestDocs.some(
            (ld) =>
              (docGuid && getDocumentGuid(ld) === docGuid) || (!docGuid && getDocName(ld) === name),
          );
          const isManual = manualSources.some((ms: ManualSource) => {
            const manualGuid = getDocumentGuid(ms);
            return (docGuid && manualGuid === docGuid) || (!docGuid && getDocName(ms) === name);
          });
          const wasSelected = prev.has(idx);
          if (isInLatest) {
            next.add(idx); // 当前消息引用的文档：默认选中
          } else if (isManual && wasSelected) {
            next.add(idx); // 手动添加且之前选中的：保持选中
          }
          // 历史文档（不在 latest 中）：不选中
        });
        return next;
      });
    }
  }, [displayDocs, latestDocs, manualSources, selectedKeys, setSelectedSources]);

  // 当手动添加新文档时，默认选中它们
  useEffect(() => {
    const manualKey = manualSources
      .map((d: ManualSource) => getDocumentGuid(d) || getDocName(d))
      .sort()
      .join("||");
    const previousManualKey = prevManualSourcesKeysRef.current.get(selectedKeys) ?? "";
    if (manualKey !== previousManualKey) {
      prevManualSourcesKeysRef.current.set(selectedKeys, manualKey);
      setSelectedSources((prev) => {
        const next = new Set(prev);
        displayDocs.forEach((doc, idx) => {
          const docGuid = getDocumentGuid(doc);
          const name = getDocName(doc);
          const isManual = manualSources.some((ms: ManualSource) => {
            const manualGuid = getDocumentGuid(ms);
            return (docGuid && manualGuid === docGuid) || (!docGuid && getDocName(ms) === name);
          });
          if (isManual && !next.has(idx)) {
            next.add(idx);
          }
        });
        return next;
      });
    }
  }, [displayDocs, manualSources, selectedKeys, setSelectedSources]);

  useEffect(() => {
    const has = selectedSources.size > 0;
    setHasSelectedSources?.(has);
  }, [selectedSources, setHasSelectedSources]);

  useEffect(() => {
    const ids = Array.from(selectedSources)
      .map((idx) => (displayDocs as any[])?.[idx])
      .map((doc: any) => getDocumentGuid(doc) || undefined)
      .filter((x: any) => typeof x === "string" && x.trim().length > 0)
      .map((x: string) => x.trim());
    const unique = Array.from(new Set(ids));
    setSelectedSourceGuidNames?.(unique);
  }, [selectedSources, displayDocs, setSelectedSourceGuidNames]);

  // 把“当前选中文档行数”推送到 context，供发送按钮左侧 / 侧栏收起浮动按钮的 Sources 徽标统一展示
  useEffect(() => {
    setSelectedSourcesCount?.(selectedSources.size);
  }, [selectedSources, setSelectedSourcesCount]);

  useEffect(() => {
    setAvailableSourcesCount(displayDocs.length);
  }, [displayDocs.length, setAvailableSourcesCount]);

  const allChecked = displayDocs.length > 0 && selectedSources.size === displayDocs.length;
  const indeterminate = selectedSources.size > 0 && selectedSources.size < displayDocs.length;
  const sourcesCountText = selectedSources.size > 99 ? "99+" : String(selectedSources.size);
  const totalSourcesCountText = displayDocs.length > 99 ? "99+" : String(displayDocs.length);

  const PAGE_SIZE = 20;
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchDocItem[]>([]);
  const [selectedSearchIds, setSelectedSearchIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [addSourcesScope, setAddSourcesScope] = useState<AskScope | null>(null);
  const [selectedAddSourceTypes, setSelectedAddSourceTypes] = useState<string[] | null>([]);
  const [selectedAddSourceStudies, setSelectedAddSourceStudies] = useState<string[] | null>([]);
  const [includePersonalDocs, setIncludePersonalDocs] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchKeywordRef = useRef(searchKeyword);
  const searchRequestIdRef = useRef(0);
  const addSourcesFilterRef = useRef<{
    docCategoryIds?: string[];
    studySourceIds?: string[];
    includePersonalDocs?: boolean;
  }>({});
  searchKeywordRef.current = searchKeyword;

  const getExtensionFromName = (name: string) => {
    const idx = name.lastIndexOf(".");
    if (idx <= 0 || idx === name.length - 1) return "";
    return name.slice(idx);
  };

  const fetchAddSourcesDocs = useCallback(
    async (page: number, keyword?: string, scopeFilters = addSourcesFilterRef.current) => {
      const requestId = ++searchRequestIdRef.current;
      if (page === 1) setSearching(true);
      else setLoadingMore(true);
      try {
        const params = buildAddSourcesSearchParams({
          page,
          limit: PAGE_SIZE,
          keyword,
          ...scopeFilters,
        });
        const res = await searchDocumentList(params);
        if (requestId !== searchRequestIdRef.current) return;
        const list = (res as any)?.Data;
        const total = Number((res as any)?.Count) || 0;
        const mapped: SearchDocItem[] = Array.isArray(list)
          ? (list
              .map((d: any) => {
                const guidName = d?.DocumentList_DocumentGuid;
                if (!guidName) return null;
                const name = String(d?.DocumentList_FileName ?? "");
                const dataSource = d?.DataSource ? String(d.DataSource) : undefined;
                const fileSrcUrl = d?.FileSrcURL ? String(d.FileSrcURL) : undefined;
                const sourcePathAskId = String(
                  d?.SourcePathAskId ?? d?.DocumentList_SourcePathAskId ?? "",
                ).trim();
                const docCategoryIds = getDocumentCategoryIds(d);
                return {
                  id: String(guidName),
                  name,
                  size: formatFileSize(d?.DocumentList_FileSize),
                  extension: getExtensionFromName(name),
                  updatedAt: String(d?.DocumentList_FileUpdateTime ?? ""),
                  fileUrl: d?.DocumentList_DownloadURL
                    ? String(d.DocumentList_DownloadURL)
                    : undefined,
                  dataSource,
                  dataSourceLabel: dataSource ? getDocumentDataSourceLabel(dataSource) : undefined,
                  dataSourceLink: getDocumentDataSourceLink(dataSource, fileSrcUrl),
                  fileSrcUrl,
                  sourcePathAskId: sourcePathAskId || undefined,
                  docCategoryIds,
                };
              })
              .filter(Boolean) as SearchDocItem[])
          : [];
        if (page === 1) {
          setSearchResults(mapped);
          setHasMore(mapped.length < total);
        } else {
          setSearchResults((prev) => {
            const next = [...prev, ...mapped];
            setHasMore(next.length < total);
            return next;
          });
        }
        setCurrentPage(page);
      } catch (e) {
        if (requestId !== searchRequestIdRef.current) return;
        console.error("searchDocumentList failed", e);
        if (page === 1) {
          messageRef.current.error(t("opsmate.docs.loadFailed"));
          setSearchResults([]);
        }
        setHasMore(false);
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setSearching(false);
          setLoadingMore(false);
        }
      }
    },
    [t],
  );

  useEffect(() => {
    if (!addSourcesVisible) {
      setSearchKeyword("");
      setSelectedSearchIds(new Set());
      setSearchResults([]);
      setCurrentPage(1);
      setHasMore(true);
      setLoadingMore(false);
      setAddSourcesScope(null);
      setSelectedAddSourceTypes([]);
      setSelectedAddSourceStudies([]);
      setIncludePersonalDocs(true);
      addSourcesFilterRef.current = {};
      searchRequestIdRef.current += 1;
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
      return;
    }
    setSearchKeyword("");
    setSelectedSearchIds(new Set());
    setAddSourcesScope(null);
    setSelectedAddSourceTypes([]);
    setSelectedAddSourceStudies([]);
    setIncludePersonalDocs(true);
    addSourcesFilterRef.current = { includePersonalDocs: false };
    fetchAddSourcesDocs(1, "", { includePersonalDocs: false });

    let cancelled = false;
    getAskScope()
      .then((scope) => {
        if (!cancelled) setAddSourcesScope(scope);
      })
      .catch((error) => {
        // 筛选配置失败时保留原有的“全部授权来源”查询，不阻断 Add Sources。
        console.error("getAskScope for Add Sources failed", error);
      });
    return () => {
      cancelled = true;
    };
  }, [addSourcesVisible, fetchAddSourcesDocs]);

  useEffect(() => {
    if (!addSourcesVisible || !addSourcesScope) return;

    const scopeFilters = {
      docCategoryIds: resolveEffectiveCategoryIds(
        addSourcesScope.generalDocs.visible,
        selectedAddSourceTypes,
        addSourcesScope.generalDocs.categories.map((category) => category.id),
      ),
      studySourceIds: resolveEffectiveStudySourceIds(
        addSourcesScope.studySource.visible,
        selectedAddSourceStudies,
        addSourcesScope.studySource.studies,
      ),
      includePersonalDocs: addSourcesScope.personalDocs.visible && includePersonalDocs,
    };
    addSourcesFilterRef.current = scopeFilters;
    setSelectedSearchIds(new Set());
    setSearchResults([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchAddSourcesDocs(1, searchKeywordRef.current, scopeFilters);
  }, [
    addSourcesScope,
    addSourcesVisible,
    fetchAddSourcesDocs,
    includePersonalDocs,
    selectedAddSourceStudies,
    selectedAddSourceTypes,
  ]);

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchResults([]);
      setCurrentPage(1);
      setHasMore(true);
      fetchAddSourcesDocs(1, keyword);
    }, 300);
  };

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      if (hasMore && !loadingMore && !searching) {
        fetchAddSourcesDocs(currentPage + 1, searchKeywordRef.current);
      }
    }
  }, [hasMore, loadingMore, searching, currentPage, fetchAddSourcesDocs]);

  const handleToggleSearchSelect = (id: string) => {
    setSelectedSearchIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirmAddSources = () => {
    const selectedDocs = searchResults.filter((doc) => selectedSearchIds.has(doc.id));
    if (selectedDocs.length > 0) {
      if (typeof setManualSources === "function") {
        setManualSources((prev: ManualSource[]) => {
          const existed = new Set(
            (prev || []).map((d: ManualSource) => getDocumentGuid(d) || getDocName(d)),
          );
          const next: ManualSource[] = [...(prev || [])];
          selectedDocs.forEach((d) => {
            const key = getDocumentGuid(d) || getDocName(d);
            if (!existed.has(key)) {
              existed.add(key);
              next.push({
                name: d.name,
                size: d.size,
                uploadedAt: d.updatedAt,
                fileUrl: d.fileUrl,
                guidName: d.id,
                sourcePathAskId: d.sourcePathAskId,
                docCategoryIds: d.docCategoryIds,
                dataSource: d.dataSource,
              });
            }
          });
          return next;
        });
      }
    }
    setAddSourcesVisible(false);
    setSelectedSearchIds(new Set());
  };

  const showSidebar = true;

  // 收起态：侧栏收窄为 44px 竖条，仅保留展开按钮与已选来源数量角标（对齐 AskSourcesPanel 逻辑）
  if (docsCollapsed) {
    return (
      <div
        style={{
          height: pageHeight,
          width: 44,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 12,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={() => setDocsCollapsed(false)}
          title={t("opsmate.docs.expandSources")}
          className="hover:bg-black/5 transition-colors"
          style={{
            position: "relative",
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
            border: "none",
            background: "transparent",
            cursor: "pointer",
          }}
        >
          <i className="ri-attachment-2 text-lg" style={{ color: "#6b7280" }}></i>
          {selectedSources.size > 0 ? (
            <span
              style={{
                position: "absolute",
                top: -4,
                right: -4,
                minWidth: 16,
                height: 14,
                padding: "0 3px",
                background: "#830051",
                borderRadius: 7,
                border: "1px solid #F8F2F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
                fontWeight: 600,
                fontSize: 10,
                lineHeight: 1,
                color: "#fff",
              }}
            >
              {sourcesCountText}
            </span>
          ) : null}
        </button>
      </div>
    );
  }

  return (
    <>
      {showSidebar && (
        <div
          style={{
            height: pageHeight,
            width: 240,
            background: "rgba(242, 229, 238, 0.35)",
            display: "flex",
            flexDirection: "column",
            paddingInline: 10,
            paddingBlock: 12,
            borderRadius: 8,
            transition: "justify-content 0.2s ease",
          }}
        >
          <div>
            <Flex justify="space-between" align="center" style={{ marginBottom: 14 }}>
              <Tooltip
                placement="bottomLeft"
                color="#fff"
                styles={{
                  container: {
                    backgroundColor: "#fff",
                    border: "1px solid #f0e6ec",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  },
                }}
                title={
                  <>
                    <p className="font-medium text-gray-600 mb-1.5">About sources</p>
                    <p className="text-gray-500">
                      Responses are generated from your selected sources. Currently includes
                      uni-managed SharePoint documents. Personal folder uploads are coming soon.
                      Data refreshes hourly.
                    </p>
                  </>
                }
              >
                <div className="flex items-center gap-2" style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{t("opsmate.docs.sources")}</div>
                  {displayDocs.length > 0 ? (
                    <span
                      style={{
                        fontSize: 11,
                        padding: "1px 6px",
                        borderRadius: 10,
                        color: "#fff",
                        fontWeight: 600,
                        background: "#830051",
                        lineHeight: "14px",
                      }}
                    >
                      {totalSourcesCountText}
                    </span>
                  ) : null}
                  <i className="ri-question-line text-xs" style={{ color: "#830051" }}></i>
                </div>
              </Tooltip>
              <button
                type="button"
                onClick={() => setDocsCollapsed(true)}
                title={t("opsmate.docs.collapseSources")}
                className="hover:bg-black/5 transition-colors"
                style={{
                  position: "relative",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 8,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <i className="ri-attachment-2 text-lg" style={{ color: "#6b7280" }}></i>
                {selectedSources.size > 0 ? (
                  <span
                    style={{
                      position: "absolute",
                      top: -4,
                      right: -4,
                      minWidth: 16,
                      height: 14,
                      padding: "0 3px",
                      background: "#830051",
                      borderRadius: 7,
                      border: "1px solid #F8F2F6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxSizing: "border-box",
                      fontWeight: 600,
                      fontSize: 10,
                      lineHeight: 1,
                      color: "#fff",
                    }}
                  >
                    {sourcesCountText}
                  </span>
                ) : null}
              </button>
            </Flex>
            <Flex
              justify="space-between"
              align="center"
              style={{
                marginBottom: 12,
                borderTop: "1px solid rgba(255,255,255,0.7)",
                borderBottom: "1px solid rgba(255,255,255,0.7)",
                paddingBlock: 8,
                paddingInline: 2,
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 12 }}>{t("opsmate.docs.selectAll")}</div>
              <Checkbox
                checked={allChecked}
                indeterminate={indeterminate}
                disabled={displayDocs.length === 0}
                onChange={(e) => {
                  if (e.target.checked)
                    setUserSelectedSources(
                      new Set(Array.from({ length: displayDocs.length }, (_, i) => i)),
                    );
                  else setUserSelectedSources(new Set());
                }}
              />
            </Flex>
          </div>
          <div className={styles.hideScrollbar}>
            {displayDocs.length === 0 ? (
              <div
                style={{
                  padding: "24px 12px",
                  textAlign: "center",
                  minHeight: 180,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="ri-file-search-line"
                  style={{ fontSize: 24, color: "#9ca3af", marginBottom: 8 }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12, lineHeight: "18px" }}>
                  {t("opsmate.docs.emptyDesc")}
                </Typography.Text>
                {/* <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {t("opsmate.docs.no_data")}
                </Typography.Text> */}
              </div>
            ) : (
              displayDocs.map((doc, index) => {
                const checked = selectedSources.has(index);
                const metadataText = getDocMetadataText(doc);
                return (
                  <Tooltip
                    key={getDocumentGuid(doc) || getDocName(doc)}
                    title={getDocTooltipTitle(doc)}
                    placement="right"
                    styles={{
                      container: {
                        background: "#fff",
                        borderRadius: 12,
                        padding: "4px 16px",
                        boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                        color: "rgba(0,0,0,0.85)",
                        fontSize: 12,
                        maxWidth: 420,
                        minWidth: 260,
                      },
                    }}
                    arrow={false}
                  >
                    <div
                      ref={(el) => {
                        if (el) docItemRefs.current.set(index, el);
                        else docItemRefs.current.delete(index);
                      }}
                      className={`${styles.sourceItem} ${checked ? styles.sourceItemChecked : ""}`}
                      style={{
                        padding: "8px 6px",
                        marginBottom: 6,
                        borderRadius: 8,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        ...(highlightedDocIndex === index
                          ? {
                              background: "rgba(131, 0, 80, 0.12)",
                            }
                          : {}),
                      }}
                      onClick={() => {
                        if (highlightedDocIndex === index) setHighlightedDocGuid("");
                        const next = new Set(selectedSources);
                        if (next.has(index)) next.delete(index);
                        else next.add(index);
                        setUserSelectedSources(next);
                      }}
                    >
                      <Flex align="flex-start" justify="space-between" gap={8}>
                        <Flex align="flex-start" gap={8} style={{ minWidth: 0, flex: 1 }}>
                          <Checkbox
                            checked={checked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              const next = new Set(selectedSources);
                              if (e.target.checked) next.add(index);
                              else next.delete(index);
                              setUserSelectedSources(next);
                            }}
                            style={{ marginTop: 2 }}
                          />
                          {getDocIcon(getDocName(doc))}
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Typography.Text
                              style={{
                                display: "block",
                                fontSize: 12,
                                color: "rgba(0,0,0,0.85)",
                                lineHeight: "18px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {getDocName(doc)}
                            </Typography.Text>
                            {metadataText ? (
                              <Typography.Text
                                type="secondary"
                                style={{
                                  display: "block",
                                  fontSize: 10,
                                  lineHeight: "16px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {metadataText}
                              </Typography.Text>
                            ) : null}
                          </div>
                        </Flex>
                        <Flex align="center" gap={8} style={{ flexShrink: 0, paddingTop: 2 }}>
                          {checked && canShowDownload(doc) ? (
                            <DownloadOutlined
                              style={{ fontSize: 14, color: "rgba(131, 0, 80, 1)" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = getDocUrl(doc);
                                if (!url) {
                                  message.warning(t("opsmate.docs.downloadUnavailable"));
                                  return;
                                }
                                window.open(
                                  resolveDownloadUrl(url),
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              }}
                            />
                          ) : null}
                        </Flex>
                      </Flex>
                    </div>
                  </Tooltip>
                );
              })
            )}
          </div>
          <ConfigProvider
            theme={{
              token: { colorText: "rgba(131, 0, 80, 1)", lineWidth: 0 },
              components: {
                Button: {
                  defaultBg: "rgba(239, 223, 234, 0.50)",
                  defaultActiveBg: "rgba(239, 223, 234, 0.50)",
                  defaultHoverBg: "rgba(239, 223, 234, 0.50)",
                  defaultActiveBorderColor: "transparent",
                },
              },
            }}
          >
            <Button
              block
              icon={<PlusOutlined />}
              size="large"
              style={{ borderWidth: 0.5, borderColor: "#fff", fontSize: 14 }}
              onClick={() => setAddSourcesVisible(true)}
            >
              {t("opsmate.docs.addSources")}
            </Button>
          </ConfigProvider>
          <div style={{ textAlign: "left", marginTop: 4 }}>
            <Typography.Text type="secondary" style={{ fontSize: 11, textAlign: "left" }}>
              <div>{t("opsmate.docs.basedOnDocs")}</div>
              {lastSyncTimeText && (
                <div style={{ whiteSpace: "pre-line" }}>
                  {t("opsmate.docs.sourceTips", { time: lastSyncTimeText })}
                </div>
              )}
            </Typography.Text>
          </div>
        </div>
      )}

      <Modal
        open={addSourcesVisible}
        title={null}
        onCancel={() => setAddSourcesVisible(false)}
        footer={null}
        width={800}
        destroyOnHidden={true}
      >
        <div>
          <Typography.Title level={5} style={{ marginBottom: 8, fontWeight: 600, fontSize: 18 }}>
            {t("opsmate.docs.addSourcesTitle")}
          </Typography.Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 16,
              marginBottom: 12,
            }}
          >
            {addSourcesScope ? (
              <AddSourcesScopeFilters
                sourceTypes={addSourcesScope.generalDocs.categories.map((category) => ({
                  id: category.id,
                  name: category.name,
                }))}
                selectedTypes={selectedAddSourceTypes}
                onTypesChange={setSelectedAddSourceTypes}
                availableStudies={addSourcesScope.studySource.studies.map((study) => ({
                  id: study.id,
                  code: study.code,
                  name: study.name,
                  studySourceIds: study.studySourceIds,
                }))}
                selectedStudies={selectedAddSourceStudies}
                onStudiesChange={setSelectedAddSourceStudies}
                includePersonalDocs={includePersonalDocs}
                onIncludePersonalDocsChange={setIncludePersonalDocs}
                showGeneral={
                  addSourcesScope.generalDocs.visible &&
                  addSourcesScope.generalDocs.categories.length > 0
                }
                showStudies={
                  addSourcesScope.studySource.visible &&
                  addSourcesScope.studySource.studies.length > 0
                }
                showPersonal={addSourcesScope.personalDocs.visible}
              />
            ) : null}
            <Input
              placeholder={t("opsmate.docs.searchPlaceholder")}
              allowClear
              value={searchKeyword}
              onChange={(e) => handleSearch(e.target.value)}
              size="large"
              prefix={<SearchOutlined style={{ color: "#8c8c8c", fontSize: 16 }} />}
              style={{ flex: "1.2 1 0", minWidth: 0 }}
            />
          </div>
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{ height: 360, overflowY: "auto" }}
          >
            <List
              dataSource={searchResults}
              split={false}
              locale={{
                emptyText: searching ? (
                  <Flex align="center" justify="center" style={{ padding: 40 }} gap={8}>
                    <Spin size="small" />
                  </Flex>
                ) : (
                  <div
                    style={{
                      padding: 40,
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      color: "rgba(0,0,0,0.45)",
                    }}
                  >
                    <Typography.Text style={{ color: "#999" }}>
                      {t("opsmate.docs.noData")}
                    </Typography.Text>
                  </div>
                ),
              }}
              renderItem={(item) => {
                const checked = selectedSearchIds.has(item.id);
                return (
                  <List.Item
                    style={{
                      paddingInline: 8,
                      cursor: "pointer",
                      background: checked ? "rgba(131, 0, 80, 0.06)" : "transparent",
                      borderRadius: 6,
                      marginBottom: 6,
                      boxShadow: "unset",
                    }}
                    onClick={() => handleToggleSearchSelect(item.id)}
                  >
                    <Flex align="center" style={{ width: "100%" }} gap={12}>
                      <Checkbox
                        checked={checked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggleSearchSelect(item.id)}
                      />
                      <Flex align="center" gap={12} style={{ flex: 1, minWidth: 0 }}>
                        {getDocIcon(item.name)}
                        <div style={{ minWidth: 0 }}>
                          <Typography.Text
                            style={{
                              display: "block",
                              fontSize: 14,
                              lineHeight: "20px",
                              color: "#111111",
                              whiteSpace: "normal",
                              wordBreak: "break-all",
                              overflowWrap: "anywhere",
                            }}
                            title={item.name}
                          >
                            {item.name}
                          </Typography.Text>
                          <Space
                            split={
                              <Divider
                                type="vertical"
                                style={{ borderColor: "#ddd", marginInline: 0 }}
                              />
                            }
                            style={{ fontSize: 12, color: "#666666", lineHeight: "16px" }}
                          >
                            <Typography.Text style={{ fontSize: 12, color: "#666666" }}>
                              {item.size}
                            </Typography.Text>
                            <Typography.Text style={{ fontSize: 12, color: "#666666" }}>
                              {item.extension}
                            </Typography.Text>
                            <Typography.Text style={{ fontSize: 12, color: "#666666" }}>
                              {item.updatedAt}
                            </Typography.Text>
                            {item.dataSource ? (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  minWidth: 0,
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Tooltip title={item.fileSrcUrl || item.dataSource} placement="top">
                                  <Typography.Text
                                    style={{
                                      fontSize: 12,
                                      color: "#666666",
                                      cursor: "default",
                                    }}
                                  >
                                    {item.dataSourceLabel || item.dataSource}
                                  </Typography.Text>
                                </Tooltip>
                                {item.dataSourceLink ? (
                                  <a
                                    href={item.dataSourceLink.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={t(
                                      item.dataSourceLink.type === "teams"
                                        ? "opsmate.docs.openTeamsChannel"
                                        : "opsmate.docs.openSharePointSite",
                                    )}
                                    title={t(
                                      item.dataSourceLink.type === "teams"
                                        ? "opsmate.docs.openTeamsChannel"
                                        : "opsmate.docs.openSharePointSite",
                                    )}
                                    className="inline-flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-md text-[#830051] transition-colors hover:bg-[#830051]/5"
                                  >
                                    <i className="ri-external-link-line" style={{ fontSize: 12 }} />
                                  </a>
                                ) : null}
                              </span>
                            ) : null}
                          </Space>
                        </div>
                      </Flex>
                    </Flex>
                  </List.Item>
                );
              }}
            />
            {loadingMore && (
              <Flex align="center" justify="center" style={{ padding: 12 }} gap={8}>
                <Spin size="small" />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t("opsmate.docs.loadingMore")}
                </Typography.Text>
              </Flex>
            )}
            {!hasMore && searchResults.length > 0 && !searching && (
              <Typography.Text
                type="secondary"
                style={{ display: "block", textAlign: "center", padding: 12, fontSize: 12 }}
              >
                {t("opsmate.docs.allLoaded")}
              </Typography.Text>
            )}
          </div>
          <Divider style={{ borderColor: "#ddd", marginBlock: 10, marginTop: 0 }} />
          <Flex justify="space-between" align="center">
            <Typography.Text
              type="secondary"
              style={{ fontSize: 14, color: "rgba(102, 102, 102, 1)" }}
            >
              {t("opsmate.docs.selected")}{" "}
              <Typography.Text style={{ color: "#111", fontWeight: "bold" }}>
                {selectedSearchIds.size}
              </Typography.Text>
            </Typography.Text>
            <Flex gap={8}>
              <Button onClick={() => setAddSourcesVisible(false)}>
                {t("opsmate.docs.cancel")}
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmAddSources}
                disabled={selectedSearchIds.size === 0}
                style={{ boxShadow: "unset" }}
              >
                {t("opsmate.docs.confirm")}
              </Button>
            </Flex>
          </Flex>
        </div>
      </Modal>
    </>
  );
}
